import { CardContent } from "@material-ui/core";
import classnames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { Loading, useNotify, useTranslate } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";
import {
  DriverApiFactory,
  DriverListDto,
  DriverLogDetailedView,
} from "../../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { CompactFormToolbar } from "../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../common/form/CompactSaveButton";
import { HookFormDriverPicker } from "../../../../common/form/hook-form/inputs/HookFormDriverPicker";
import { HookFormSelectInput } from "../../../../common/form/hook-form/inputs/HookFormSelectInput";

interface Props {
  record: DriverLogDetailedView;
  onSuccess: (targetDriver: number) => void;
  onFailed?: () => void;
}

type FormValues = {
  fromDriverId: number;
  toCoDriverId: number;
};

const DefaultCompactFormToolbar = () => {
  const ctx = useFormContext();
  const {
    formState: { isSubmitting },
  } = ctx;

  return (
    <CompactFormToolbar>
      <CompactSaveButton saving={isSubmitting} disabled={isSubmitting} />
    </CompactFormToolbar>
  );
};

export const RolloverForm = ({
  record,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const axios = useAxiosInstance();
  const [data, setData] = useState<DriverListDto[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const formContext = useForm<FormValues>({
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!axios) {
      return;
    }

    setLoading(true);
    const driverIds = [
      record.driverId!,
      ...(record.coDrivers ?? []).filter((x) => !!x.id).map((x) => x.id!),
    ];

    DriverApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppDriverGetManyGet(driverIds)
      .then((response) => setData(response.data))
      .finally(() => setLoading(false));
  }, [axios, record]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      return DriverApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppDriverRolloverPost(data)
        .then(() => {
          onSuccess(data.toCoDriverId);
        })
        .catch((e: any) => {
          if (onFailed) {
            onFailed();
          }

          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, notify, onSuccess, onFailed]
  );

  const { handleSubmit, control } = formContext;
  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  if (loading) {
    return (
      <CardContent>
        <Loading />
      </CardContent>
    );
  }

  if (!data || data.every((x) => !x) || !record) {
    return null;
  }

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <div className="ra-input">
            <HookFormSelectInput<FormValues>
              control={control}
              name="fromDriverId"
              choices={data.filter((x) => x.id === record.driverId)}
              label="From driver"
              rules={{ required: translate("ra.validation.required") }}
              optionText={(x) => (x ? x.driverName : "No available driver")}
              variant="outlined"
              defaultValue={record.driverId}
              disabled
            />
          </div>
          <div className="ra-input">
            <HookFormDriverPicker<FormValues>
              control={control}
              name="toCoDriverId"
              label="To driver"
              rules={{ required: translate("ra.validation.required") }}
              variant="outlined"
              defaultValue={null}
            />
          </div>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
